import { TrendingUp, TrendingDown } from 'lucide-react'

interface HealthMetric {
  label: string
  value: string | number
  change: string
  // `flat` means no real period-over-period data is available yet
  // (backend returns "flat" until the snapshot history table lands).
  // The card hides the delta indicator in that case so we don't show
  // a misleading "+0" arrow.
  trend: 'up' | 'down' | 'flat'
  color: 'green' | 'red' | 'yellow'
}

interface HealthMetricsGridProps {
  metrics: HealthMetric[]
  onMetricClick?: (metric: HealthMetric) => void
}

export function HealthMetricsGrid({ metrics, onMetricClick }: HealthMetricsGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric, index) => (
        <div
          key={index}
          onClick={() => onMetricClick?.(metric)}
          className={`bg-white p-6 rounded-xl border-2 border-[#bcccdc] hover:border-[#486581] hover:shadow-lg transition-all ${
            onMetricClick ? 'cursor-pointer' : ''
          }`}
        >
          <p className="text-sm text-[#829ab1] mb-2">{metric.label}</p>
          <div className="text-2xl mb-1 text-[#102a43] font-medium">{metric.value}</div>
          {metric.trend === 'flat' ? (
            // No baseline yet — keep the row height consistent so cards
            // don't pop. Surface "—" instead of a green/red arrow.
            <div className="text-sm flex items-center gap-1 font-medium text-[#829ab1]">
              <span aria-hidden="true">—</span>
              <span className="sr-only">No change data available</span>
            </div>
          ) : (
            <div
              className={`text-sm flex items-center gap-1 font-medium ${
                metric.trend === 'up' && metric.color === 'green'
                  ? 'text-green-600'
                  : metric.trend === 'down' && metric.color === 'red'
                    ? 'text-red-600'
                    : 'text-yellow-600'
              }`}
            >
              {metric.trend === 'up' ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              {metric.change}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

